// downloadHtml.js - Enhanced with Chart Support
import html2canvas from "html2canvas";

// ---------- Common timestamp helpers ----------
function makeTimestamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return [
    d.getFullYear(),
    "-",
    pad(d.getMonth() + 1),
    "-",
    pad(d.getDate()),
    "_",
    pad(d.getHours()),
    "-",
    pad(d.getMinutes()),
    "-",
    pad(d.getSeconds()),
  ].join("");
}

export function makeTimestampedName(base = "export", ext = "html") {
  return `${base}_${makeTimestamp()}.${ext}`;
}

/**
 * @typedef {Object} DownloadHtmlOptions
 * @property {string} [filename] - Output filename
 * @property {boolean} [selfContained=false] - Inline CSS into <style>
 * @property {boolean} [includeExternalCssLinks] - Include <link> tags
 * @property {string} [extraHeadHtml] - Extra tags for <head>
 * @property {boolean} [captureCharts=true] - Convert charts to images
 */

/**
 * Enhanced HTML download with chart capture support
 */
export async function downloadNodeAsHtml(nodeOrRef, opts = {}) {
  const node =
    nodeOrRef && "current" in nodeOrRef ? nodeOrRef.current : nodeOrRef;

  if (!node || !(node instanceof HTMLElement)) {
    throw new Error("downloadNodeAsHtml: target element not found or invalid");
  }

  const {
    filename = makeTimestampedName("export", "html"),
    selfContained = false,
    includeExternalCssLinks = !selfContained,
    extraHeadHtml = '<meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">',
    captureCharts = true,
  } = opts;

  // 🔥 NEW: Capture charts as images before HTML export
  let modifiedBodyContent = node.outerHTML;

  if (captureCharts) {
    try {
      modifiedBodyContent = await captureChartsAsImages(node);
    } catch (error) {
      console.warn(
        "Chart capture failed, proceeding with original content:",
        error
      );
      modifiedBodyContent = node.outerHTML;
    }
  }

  // Get styles (same as before)
  const inlineStyleTagsFromDoc = Array.from(document.querySelectorAll("style"))
    .map(
      (style) =>
        `<style${copyAttributes(style)}>${style.textContent || ""}</style>`
    )
    .join("\n");

  const linkTags = includeExternalCssLinks
    ? Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .map((link) => `<link rel="stylesheet" href="${link.href}">`)
        .join("\n")
    : "";

  let criticalCss = "";
  if (selfContained) {
    try {
      for (const sheet of Array.from(document.styleSheets)) {
        let rules;
        try {
          rules = sheet.cssRules;
        } catch {
          continue;
        }
        if (!rules) continue;
        criticalCss += Array.from(rules)
          .map((r) => r.cssText)
          .join("\n");
      }
    } catch {
      // Ignore stylesheet access failures
    }
  }
  const criticalStyleTag =
    selfContained && criticalCss ? `<style>${criticalCss}</style>` : "";

  // Build HTML document
  const htmlDoc =
    "<!doctype html>" +
    `<html lang="en">` +
    `<head>` +
    `${extraHeadHtml}` +
    `${linkTags}` +
    `${inlineStyleTagsFromDoc}` +
    `${criticalStyleTag}` +
    `</head>` +
    `<body>${modifiedBodyContent}</body>` +
    `</html>`;

  // Save the file
  const blob = new Blob([htmlDoc], { type: "text/html" });
  const savedWithPicker = await saveBlobWithPicker(blob, filename);
  if (savedWithPicker) return;

  // Fallback download
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/**
 * 🔥 NEW FUNCTION: Capture charts as images and replace in DOM
 */
/**
 * Enhanced chart capture specifically for SAST dashboard
 */
async function captureChartsAsImages(node) {
  const tempContainer = document.createElement("div");
  tempContainer.style.position = "absolute";
  tempContainer.style.top = "-9999px";
  tempContainer.style.left = "-9999px";
  tempContainer.style.width = "1200px";
  tempContainer.style.backgroundColor = "#ffffff";

  const clonedNode = node.cloneNode(true);
  tempContainer.appendChild(clonedNode);
  document.body.appendChild(tempContainer);

  try {
    // 🔥 CAPTURE SEVERITY CIRCLES SECTION
    const severitySection =
      node.querySelector('[sx*="backgroundColor"][sx*="#f6f3f2"]') ||
      node.querySelector('div[style*="background-color: rgb(246, 243, 242)"]');

    if (severitySection) {
      const canvas = await html2canvas(severitySection, {
        useCORS: true,
        scale: 2,
        backgroundColor: "#f6f3f2",
        logging: false,
        allowTaint: false,
        foreignObjectRendering: true,
      });

      const severityImageData = canvas.toDataURL("image/png", 1.0);

      // Replace in clone
      const clonedSeveritySection =
        clonedNode.querySelector('[sx*="backgroundColor"][sx*="#f6f3f2"]') ||
        clonedNode.querySelector(
          'div[style*="background-color: rgb(246, 243, 242)"]'
        );

      if (clonedSeveritySection) {
        const img = document.createElement("img");
        img.src = severityImageData;
        img.style.width = "100%";
        img.style.height = "auto";
        img.style.display = "block";
        img.alt = "Severity Summary";
        clonedSeveritySection.innerHTML = "";
        clonedSeveritySection.appendChild(img);
      }
    }

    // 🔥 CAPTURE BAR CHART
    const chartElement = node.querySelector(".echarts-for-react");
    if (chartElement) {
      let chartImageData = null;

      // Try ECharts native method first
      if (chartElement._echartsInstance) {
        try {
          chartImageData = chartElement._echartsInstance.getDataURL({
            type: "png",
            pixelRatio: 2,
            backgroundColor: "#ffffff",
          });
        } catch (e) {
          console.log("ECharts getDataURL failed, using html2canvas");
        }
      }

      // Fallback to html2canvas
      if (!chartImageData) {
        const canvas = await html2canvas(chartElement, {
          useCORS: true,
          scale: 2,
          backgroundColor: "#FFFFFF",
          logging: false,
          allowTaint: false,
        });
        chartImageData = canvas.toDataURL("image/png", 1.0);
      }

      if (chartImageData) {
        const clonedChart = clonedNode.querySelector(".echarts-for-react");
        if (clonedChart) {
          const img = document.createElement("img");
          img.src = chartImageData;
          img.style.width = "100%";
          img.style.height = "300px";
          img.style.display = "block";
          img.alt = "Vulnerability Count By Severity Chart";
          clonedChart.innerHTML = "";
          clonedChart.appendChild(img);
        }
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 1000));
    return clonedNode.outerHTML;
  } finally {
    document.body.removeChild(tempContainer);
  }
}

/**
 * Preserve attributes from elements
 */
function copyAttributes(el) {
  if (!el || !el.hasAttributes()) return "";
  return (
    " " +
    Array.from(el.attributes)
      .map((a) => `${a.name}="${a.value}"`)
      .join(" ")
  );
}

/**
 * Native Save As via File System Access API with proper cancellation handling
 */
async function saveBlobWithPicker(blob, suggestedName = "export.html") {
  const supportsPicker =
    "showSaveFilePicker" in window &&
    (() => {
      try {
        return window.self === window.top;
      } catch {
        return false;
      }
    })();

  if (!supportsPicker) return { success: false, cancelled: false };

  try {
    const handle = await window.showSaveFilePicker({
      suggestedName,
      types: [
        {
          description: "HTML file",
          accept: { "text/html": [".html", ".htm"] },
        },
      ],
    });

    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();

    return { success: true, cancelled: false };
  } catch (err) {
    if (err && err.name === "AbortError") {
      // User explicitly cancelled - don't fallback to download
      console.log("User cancelled HTML file save");
      return { success: false, cancelled: true };
    } else {
      // Actual error - fallback to download
      console.error("showSaveFilePicker error:", err);
      return { success: false, cancelled: false };
    }
  }
}
